import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSeoForPath, SITE_NAME } from "@/lib/seo";

/**
 * Seo — synchronously injects per-page title / meta / OG / Twitter / canonical
 * tags into <head>.
 *
 * Uses useLayoutEffect (not Helmet) so the DOM is mutated synchronously before
 * paint. This is what makes react-snap reliably capture every page's meta
 * during build-time prerender — Helmet's RAF-batched writes were being missed
 * by some snapshots.
 */
function setOrCreateMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content);
  el.setAttribute("data-seo", "true");
}

function setOrCreateLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.setAttribute("data-seo", "true");
}

export default function Seo({ title, description, image, canonical }) {
  const { pathname } = useLocation();
  const defaults = getSeoForPath(pathname);
  const finalTitle = title ? `${title} · ${SITE_NAME}` : defaults.title;
  const finalDescription = description || defaults.description;
  const finalImage = image || defaults.image;
  const finalCanonical = canonical || defaults.canonical;

  useLayoutEffect(() => {
    document.title = finalTitle;

    setOrCreateMeta('meta[name="description"]', {
      name: "description",
      content: finalDescription,
    });

    // Open Graph
    setOrCreateMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    setOrCreateMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE_NAME,
    });
    setOrCreateMeta('meta[property="og:title"]', {
      property: "og:title",
      content: finalTitle,
    });
    setOrCreateMeta('meta[property="og:description"]', {
      property: "og:description",
      content: finalDescription,
    });
    setOrCreateMeta('meta[property="og:image"]', {
      property: "og:image",
      content: finalImage,
    });
    setOrCreateMeta('meta[property="og:url"]', {
      property: "og:url",
      content: finalCanonical,
    });

    // Twitter
    setOrCreateMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    setOrCreateMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: finalTitle,
    });
    setOrCreateMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: finalDescription,
    });
    setOrCreateMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: finalImage,
    });

    // Canonical
    setOrCreateLink("canonical", finalCanonical);
  }, [finalTitle, finalDescription, finalImage, finalCanonical]);

  return null;
}
